import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { colors, typography } from '../constants';
import {
  Card,
  CardFooterButton,
  ChannelDot,
  ScreenState,
  ShareBar,
  StatCard,
} from '../components';
import { useAnalytics } from '../hooks/useProfitData';
import useProfitIqTabs from '../hooks/useProfitIqTabs';
import { useDateRange } from '../hooks/useSelection';
import { dateRangeLabel, fmt, fmtN, negativeTone, pct } from '../lib/format';

/** Net profit margin banding: comfortable, thin, losing money. */
export function netColor(npp) {
  return npp >= 15 ? colors.success : npp >= 5 ? colors.warning : colors.destructive;
}

function CompactItemRow({ item }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1.25,
        gap: 1,
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}
      >
        <Typography sx={{ fontSize: typography.heading }}>{item.emoji}</Typography>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontSize: typography.body,
              fontWeight: 600,
              color: colors.foreground,
            }}
          >
            {item.name}
          </Typography>
          <Typography sx={{ fontSize: typography.caption, color: colors.faint }}>
            {fmtN(item.unitsSold)} sold · {fmt(item.salesPrice * item.unitsSold)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography
          sx={{
            fontSize: typography.body,
            fontWeight: 700,
            color: netColor(item.netProfitPct),
          }}
        >
          {pct(item.netProfitPct)}
        </Typography>
        <Typography sx={{ fontSize: typography.caption, color: colors.faint }}>
          {fmt(item.totalProfit)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function Overview() {
  const { goToTab } = useProfitIqTabs();
  const { range, customLabel, month } = useDateRange();
  const query = useAnalytics();

  const dateLabel = dateRangeLabel(range, month, customLabel);

  return (
    <ScreenState id="overview" query={query} loadingLabel="Loading overview…">
      {(data) => (
        <>
          {/* Health score + estimated profit */}
          <Card sx={{ p: 2.5, mb: 1.75, borderRadius: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: typography.caption,
                    color: colors.mutedForeground,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                  }}
                >
                  Health Score
                </Typography>
                <Typography
                  sx={{
                    fontSize: typography.hero,
                    fontWeight: 800,
                    color: data.scoreColor,
                    lineHeight: 1.2,
                  }}
                >
                  {data.score}
                </Typography>
                <Typography
                  sx={{
                    fontSize: typography.caption,
                    color: data.scoreColor,
                    fontWeight: 600,
                  }}
                >
                  {data.scoreLabel}
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  sx={{
                    fontSize: typography.caption,
                    color: colors.mutedForeground,
                    mb: 0.4,
                  }}
                >
                  Est. profit
                </Typography>
                <Typography
                  sx={{
                    fontSize: typography.stat,
                    fontWeight: 800,
                    color:
                      data.profitDollars < 0 ? colors.destructive : colors.success,
                  }}
                >
                  {fmt(data.profitDollars)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: typography.caption,
                    color: colors.mutedForeground,
                    ...negativeTone(data.profitPct),
                  }}
                >
                  {pct(data.profitPct)} margin
                </Typography>
              </Box>
            </Box>

            {/* The bar reads against a 30% margin, which is where the score tops out. */}
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  height: 10,
                  backgroundColor: colors.muted,
                  borderRadius: 999,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${Math.max(0, data.profitPct)}%`,
                    height: '100%',
                    backgroundColor: data.scoreColor,
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 0.6,
                }}
              >
                <Typography
                  sx={{ fontSize: typography.caption, color: colors.faint }}
                >
                  0%
                </Typography>
                <Typography
                  sx={{
                    fontSize: typography.caption,
                    color: colors.faint,
                    ...negativeTone(data.profitPct),
                  }}
                >
                  {pct(data.profitPct)} margin
                </Typography>
                <Typography
                  sx={{ fontSize: typography.caption, color: colors.faint }}
                >
                  30%
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Metric tiles */}
          <Grid container spacing={1.25} sx={{ mb: 1.75 }}>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="REVENUE"
                value={fmt(data.totalRevenue)}
                sub={dateLabel}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="ORDERS"
                value={fmtN(data.totalOrders)}
                sub="total orders"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="TOTAL COSTS"
                value={pct(data.totalCostPct)}
                sub={`${fmt((data.totalRevenue * data.totalCostPct) / 100)} est.`}
              />
            </Grid>
          </Grid>

          {/* Revenue by channel */}
          <Card sx={{ mb: 1.75 }}>
            <Typography
              sx={{ fontSize: typography.body, fontWeight: 700, mb: 1.25 }}
            >
              Revenue by channel
            </Typography>
            {data.channels.map((c) => (
              <Box
                key={c.channelId}
                sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}
              >
                <ChannelDot color={c.color} size={8} />
                <Typography noWrap sx={{ flex: 1, fontSize: typography.small }}>
                  {c.name}
                </Typography>
                {c.commission > 0 ? (
                  <Typography
                    sx={{
                      fontSize: typography.caption,
                      color: colors.destructive,
                    }}
                  >
                    {/* A commission is money taken off this channel's revenue,
                        so it reads as a deduction. */}
                    {pct(-c.commission)}
                  </Typography>
                ) : null}
                <Typography
                  sx={{ fontSize: typography.small, fontWeight: 700 }}
                >
                  {fmt(c.revenue)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: typography.caption,
                    color: colors.faint,
                    minWidth: 46,
                    textAlign: 'right',
                  }}
                >
                  {pct(c.sharePct)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: typography.caption,
                    color: c.profitPct >= 15 ? colors.success : colors.warning,
                    minWidth: 52,
                    textAlign: 'right',
                    ...negativeTone(c.profitPct),
                  }}
                >
                  {pct(c.profitPct)}
                </Typography>
              </Box>
            ))}
            <CardFooterButton
              label="Edit channel commissions"
              onClick={() => goToTab('channels')}
            />
          </Card>

          {/* Cost breakdown */}
          <Card sx={{ mb: 1.75 }}>
            <Typography
              sx={{ fontSize: typography.body, fontWeight: 700, mb: 1.25 }}
            >
              Cost breakdown
            </Typography>
            {data.costCats.map((cat) => (
              <Box
                key={cat.key}
                sx={{ display: 'flex', alignItems: 'center', mb: 0.9, gap: 0.5 }}
              >
                <Typography sx={{ fontSize: typography.caption }}>
                  {cat.icon}
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    width: 130,
                    fontSize: typography.caption,
                    color: colors.mutedForeground,
                  }}
                >
                  {cat.label}
                </Typography>
                {/* The bar is scaled so 50% of revenue fills it — beyond that a
                    cost line is already the whole story. */}
                <ShareBar
                  width={Math.min(cat.totalPct, 50) * 2}
                  sx={{ flex: 1, mx: 1 }}
                />
                {/* Wide enough for "100.00%" on one line: at two decimals the
                    narrower column wrapped the sign onto a second row. */}
                <Typography
                  noWrap
                  sx={{
                    width: 58,
                    fontSize: typography.caption,
                    fontWeight: 700,
                    textAlign: 'right',
                  }}
                >
                  {pct(cat.totalPct)}
                </Typography>
              </Box>
            ))}
            <Box
              sx={{
                mt: 1.25,
                pt: 1.25,
                borderTop: `1px solid ${colors.border}`,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{ fontSize: typography.caption, fontWeight: 600 }}
              >
                Est. Profit
              </Typography>
              <Typography
                sx={{
                  fontSize: typography.body,
                  fontWeight: 800,
                  color:
                    data.profitDollars < 0 ? colors.destructive : colors.success,
                }}
              >
                {pct(data.profitPct)} · {fmt(data.profitDollars)}
              </Typography>
            </Box>
            <CardFooterButton
              label="Edit my costs"
              onClick={() => goToTab('costs')}
            />
          </Card>

          {/* Top items */}
          <Card>
            <Typography
              sx={{ fontSize: typography.body, fontWeight: 700, mb: 1.25 }}
            >
              Top items · {dateLabel}
            </Typography>
            {/* Picked by the engine, over every item that sold rather than over
                the page of them this response carries. */}
            {data.topItems.map((item) => (
              <CompactItemRow key={item.id} item={item} />
            ))}
            <CardFooterButton
              label={`See all ${data.itemsTotal} items`}
              onClick={() => goToTab('menu')}
            />
          </Card>
        </>
      )}
    </ScreenState>
  );
}
